import { FC, useMemo, useState } from 'react'
import {
  InputSelectControl,
  RadioButton,
  SimpleModal,
  useNotifications,
  Flex,
  Grid,
  GridItem,
  BorderRadius6,
  GreyGrey10,
  GreyGrey80,
  GreyGrey90,
  IndigoIndigo60,
  Primary13Regular,
  Primary13SemiBold
} from '@sequencehq/core-components'
import { getIntegrationName } from 'Integrations/utils/getIntegrationName.ts'
import { useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import { useMutation, useQuery } from '@sequencehq/api/utils'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { IntegrationServices } from '@sequencehq/api/utils/commonEnums'
import invariant from 'tiny-invariant'
import { useUpdateProduct } from 'Products/hooks/useUpdateProduct'
import {
  getSyncErrorDescription,
  type LinkOutcome,
  storeEntityLink
} from 'Integrations/integrationsConfig/common/shared'

const SERVICES_SUPPORTING_LINK_ONLY = ['Rillet'] as const
const SERVICES_SUPPORTING_LINK_AND_CREATE = ['NetSuite'] as const
/**
 * Services whose newly created item needs an income account picked up front,
 * rather than inheriting one from the service's own defaults.
 */
const SERVICES_REQUIRING_INCOME_ACCOUNT = ['NetSuite'] as const

const supportsCreate = (
  service: IntegrationServices
): service is (typeof SERVICES_SUPPORTING_LINK_AND_CREATE)[number] =>
  SERVICES_SUPPORTING_LINK_AND_CREATE.includes(
    service as (typeof SERVICES_SUPPORTING_LINK_AND_CREATE)[number]
  )

const requiresIncomeAccount = (service: IntegrationServices) =>
  SERVICES_REQUIRING_INCOME_ACCOUNT.includes(
    service as (typeof SERVICES_REQUIRING_INCOME_ACCOUNT)[number]
  )

const supportsLink = (service: IntegrationServices) =>
  supportsCreate(service) ||
  SERVICES_SUPPORTING_LINK_ONLY.includes(
    service as (typeof SERVICES_SUPPORTING_LINK_ONLY)[number]
  )

interface Props {
  onConfirm?: () => void
  onClose: () => void
  productId: string
  service: IntegrationServices
  externalEntityLabel: string
}

export const LinkOrCreateProductModal: FC<Props> = ({
  onConfirm,
  onClose,
  productId,
  service,
  externalEntityLabel
}) => {
  invariant(
    supportsLink(service),
    `Linking products is not supported for ${service}`
  )

  const canCreate = supportsCreate(service)
  const needsIncomeAccount = canCreate && requiresIncomeAccount(service)

  const serviceName = getIntegrationName(service)

  const [isLinking, setIsLinking] = useState(false)
  const { displayNotification } = useNotifications()
  const { editProduct } = useUpdateProduct()
  const pushProductMutation = useMutation(
    dashboard20240730Client.postPushProductToExternalService
  )

  const productsOnExternalServiceResponse = useQuery(
    dashboard20240730Client.getIntegrationProductsService,
    service,
    {
      select: res => res?.items ?? []
    }
  )

  const productLinkingOptions = productsOnExternalServiceResponse.data
    ?.filter(product => !product.isLinked)
    .map(product => ({
      label: product.name,
      value: product.id
    }))

  const incomeAccountsResponse = useQuery(
    dashboardv99990101Client.getNetsuiteIncomeAccounts,
    undefined,
    {
      select: res => res?.items ?? [],
      enabled: needsIncomeAccount
    }
  )

  // The backend sorts these by name.
  const incomeAccountOptions = incomeAccountsResponse.data?.map(account => ({
    label: account.name,
    value: account.id,
    secondaryLabel: account.code
  }))

  const { fields, queries } = useForm({
    value: {
      linkToProduct: '',
      createNew: false,
      incomeAccountId: ''
    },
    fieldConfiguration: [
      {
        property: 'createNew'
      },
      {
        property: 'linkToProduct',
        disabled: ({ formData }) => formData.createNew,
        validation: [required],
        options: productLinkingOptions
      },
      {
        property: 'incomeAccountId',
        disabled: ({ formData }) => !needsIncomeAccount || !formData.createNew,
        validation: [required],
        options: incomeAccountOptions
      }
    ]
  })

  const { createNew, linkToProduct, incomeAccountId } = fields

  const sequenceProductResponse = useQuery(
    dashboardv99990101Client.getProduct,
    { id: productId },
    {
      enabled: !isLinking
    }
  )

  const linkToExistingServiceProduct = async (): Promise<LinkOutcome> => {
    const sequenceProduct = sequenceProductResponse.data
    if (!sequenceProduct) {
      return { isLinked: false }
    }

    const updated = await editProduct(productId, {
      ...sequenceProduct,
      integrationIds: storeEntityLink(
        sequenceProduct.integrationIds,
        service,
        linkToProduct.value
      )
    })

    return { isLinked: updated !== null }
  }

  const pushProductToService = async (): Promise<LinkOutcome> => {
    invariant(
      supportsCreate(service),
      `Creating products is not supported for ${service}`
    )

    try {
      await pushProductMutation.mutateAsync({
        service,
        productId,
        incomeAccountId: incomeAccountId.value
      })
      return { isLinked: true }
    } catch (error) {
      return {
        isLinked: false,
        errorDescription: getSyncErrorDescription(error)
      }
    }
  }

  const externalProductDetails = useMemo(() => {
    const externalProduct = productsOnExternalServiceResponse.data?.find(
      product => product.id === linkToProduct.value
    )

    if (!externalProduct) {
      return null
    }

    return {
      name: externalProduct.name,
      externalUrl: externalProduct.externalUrl
    }
  }, [productsOnExternalServiceResponse.data, linkToProduct.value])

  const handleSubmit = async (): Promise<boolean> => {
    setIsLinking(true)

    const { isLinked, errorDescription } = await (queries.formData.createNew
      ? pushProductToService()
      : linkToExistingServiceProduct())

    if (!isLinked) {
      displayNotification(`Could not link product to ${serviceName}`, {
        type: 'error',
        description: errorDescription
      })
      setIsLinking(false)
      return false
    }

    displayNotification(`Product linked to ${serviceName}`, {
      type: 'success'
    })

    setIsLinking(false)
    onConfirm?.()
    return true
  }

  return (
    <SimpleModal
      isOpen
      onClose={onClose}
      data-testid="modal.linkProduct"
      title={
        canCreate
          ? `Link or create ${serviceName} ${externalEntityLabel}`
          : `Link ${serviceName} ${externalEntityLabel}`
      }
      cancelButtonText="Cancel"
      submitButtonText="Create link"
      submittingButtonText="Creating link..."
      submitDisabled={!queries.isValid || isLinking}
      submitting={isLinking}
      onSubmit={handleSubmit}
      contentProps={{
        minHeight: '0px'
      }}
    >
      <Flex
        color={GreyGrey80}
        {...Primary13Regular}
        flexDirection="column"
        gap="16px"
      >
        {canCreate && (
          <RadioButton
            name="createNew"
            data-testid="createNew.existing"
            label={`Select existing ${externalEntityLabel} in ${serviceName}`}
            isChecked={!createNew.value}
            onChange={() => {
              createNew.onChange(false)
            }}
          />
        )}
        {!linkToProduct.disabled ? (
          <>
            <InputSelectControl
              {...linkToProduct}
              data-testid="linkToProduct"
              placeholder={`Select ${serviceName} ${externalEntityLabel}`}
              disabled={linkToProduct.disabled}
            />
            {externalProductDetails && (
              <Grid
                gridTemplateColumns="1fr auto"
                gridColumnGap="8px"
                backgroundColor={GreyGrey10}
                borderRadius={BorderRadius6}
                padding="12px"
              >
                <GridItem
                  {...Primary13SemiBold}
                  color={GreyGrey90}
                  data-testid="modal.linkProduct.details.name"
                >
                  {externalProductDetails.name}
                </GridItem>
                <GridItem {...Primary13SemiBold} color={IndigoIndigo60}>
                  <a
                    href={externalProductDetails.externalUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="modal.linkProduct.details.link"
                  >
                    View in {serviceName}
                  </a>
                </GridItem>
              </Grid>
            )}
          </>
        ) : null}
        {canCreate && (
          <RadioButton
            name="createNew"
            data-testid="createNew.new"
            label={`Create new ${externalEntityLabel} in ${serviceName}`}
            isChecked={createNew.value}
            onChange={() => {
              createNew.onChange(true)
            }}
          />
        )}
        {!incomeAccountId.disabled ? (
          <InputSelectControl
            {...incomeAccountId}
            data-testid="incomeAccountId"
            placeholder={`Select ${serviceName} income account`}
            disabled={incomeAccountId.disabled}
          />
        ) : null}
      </Flex>
    </SimpleModal>
  )
}
